import { useState, type ReactNode } from 'react';
import {
  Button,
  FormField,
  Input,
  PasswordRequirements,
  useToast,
} from '@/shared/ui/primitives';
import { Modal } from '@/shared/ui/overlay';
import { useT } from '@/shared/i18n';
import { ApiError } from '@/shared/api/api-error';
import { useAuth } from '../hooks/useAuth';
import { changePassword } from '../services/auth.service';

/**
 * Blocking gate for accounts provisioned with a temporary password (admin
 * invitations). While `mustChangePassword` is set, the workspace is covered by
 * a non-dismissible dialog that forces the user to set their own password. On
 * success the `/me` flag clears and the gate disappears.
 */
export function ForcePasswordChangeGate(): ReactNode {
  const { t } = useT();
  const { user, refreshUser } = useAuth();
  const { notify } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user?.mustChangePassword) return null;

  async function submit(): Promise<void> {
    if (newPassword !== confirm) {
      setError(t('forcePw.mismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await changePassword({ currentPassword, newPassword });
      await refreshUser();
      notify({ tone: 'success', title: t('forcePw.success') });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('forcePw.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => {}}
      title={t('forcePw.title')}
      description={t('forcePw.subtitle')}
      footer={
        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={
            submitting ||
            !currentPassword ||
            !newPassword ||
            !confirm
          }
        >
          {submitting ? t('forcePw.submitting') : t('forcePw.submit')}
        </Button>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField label={t('forcePw.current')}>
          <Input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            autoFocus
          />
        </FormField>
        <FormField label={t('forcePw.new')}>
          <Input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
          />
        </FormField>
        <FormField label={t('forcePw.confirm')}>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
          />
        </FormField>
        <PasswordRequirements value={newPassword} confirmValue={confirm} />
        {error ? <p className="text-sm text-[var(--danger-text)]">{error}</p> : null}
      </form>
    </Modal>
  );
}
